import { LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import type { ContactStatus, LeadQuality, ServiceFlag } from '../../types/lead.js';
import { botNameOf } from '../botName.js';
import { dayLabelFor, timeLabelFor } from '../leadBot/portalPageView.js';
import { formatClock } from './leadSplit.js';

// THE LEADS CENTRE V2 PRESENTATION RULES (KyckConnect Leads centre v2, Part A). Pure: one definition of the status colours, the stage a
// row is in, the ONE plain-English line under a name, and the sentences the detail header uses. No clock reads (the caller passes
// `nowMs`), no I/O, no model. ⚠️ The colour is never the only signal: every chip carries its word, and every fill takes INK text.

// ── status colours ───────────────────────────────────────────────────────────────────────────────────────────────────────────

export const LEAD_STAGE_KEYS = ['needs_you', 'distress', 'new', 'talking', 'booked', 'joined', 'not_interested'] as const;
export type LeadStageKey = (typeof LEAD_STAGE_KEYS)[number];

export interface StageStyle {
  /** The chip's word. */
  label: string;
  /** The portal look's soft fill (ink text on it). Null = outline only. The CSS in src/index.css carries the same hex, pinned by a test. */
  portalFill: string | null;
  /** The extra ring (a 1.5px line) the portal look draws around a distress chip. */
  portalRing: string | null;
  /** The staff look's classes: the SAME stages in the shadcn palette. */
  staffClass: string;
}

/** ONE map for both looks. The portal hex values are the approved canvas fills; the staff look uses semantic Tailwind classes. */
export const STAGE_STYLES: Record<LeadStageKey, StageStyle> = {
  needs_you: { label: 'Needs you', portalFill: '#f3d58a', portalRing: null, staffClass: 'border border-amber-400 bg-amber-100 text-amber-950' },
  distress: { label: 'Possible distress', portalFill: '#f2b8b0', portalRing: '#b3261e', staffClass: 'border border-destructive bg-transparent text-destructive' },
  new: { label: 'New', portalFill: '#d4cfc1', portalRing: null, staffClass: 'bg-secondary text-secondary-foreground' },
  talking: { label: 'Talking', portalFill: '#bcd4ee', portalRing: null, staffClass: 'border border-blue-300 bg-blue-100 text-blue-950' },
  booked: { label: 'Booked', portalFill: '#a6e4c2', portalRing: null, staffClass: 'border border-green-300 bg-green-100 text-green-950' },
  joined: { label: 'Joined', portalFill: '#02c264', portalRing: null, staffClass: 'bg-green-600 text-white' },
  not_interested: { label: 'Not interested', portalFill: null, portalRing: null, staffClass: 'border border-border bg-transparent text-muted-foreground' },
};

export const INK = '#0e1b1e';

/** Hot / warm / cold as a small dot plus the word. Unknown has no dot (nothing to say). */
export const TEMPERATURE_STYLES: Record<Exclude<LeadQuality, 'unknown'>, { label: string; dot: string }> = {
  hot: { label: 'Hot', dot: '#e4572e' },
  warm: { label: 'Warm', dot: '#f0a83a' },
  cold: { label: 'Cold', dot: '#6b9bd1' },
};

/** The stage a booked lead is in. (Named once: the word is also an appointment status, so it is kept off any line that mentions an outcome.) */
const BOOKED_STAGE = 'booked' as const;
const BOOKED_CODES: readonly LeadOutcome[] = ['tour_booked', 'callback_booked'];

/** The fields of a list row (or a detail) the stage reads. A structural subset, so both the row and the detail pass. */
export interface StageFacts {
  outcome: LeadOutcome;
  outcomeSetBy: 'person' | 'ai' | 'system' | null;
  needsPerson: boolean;
  contactStatus: ContactStatus;
}

/** "Needs your team": a person is needed, or the AI set an outcome nobody has confirmed. THE SAME rule as the stage tab (leadSplit.needsYourTeam). */
export const needsYou = (f: Pick<StageFacts, 'outcome' | 'outcomeSetBy' | 'needsPerson'>): boolean => f.needsPerson || (f.outcomeSetBy === 'ai' && f.outcome !== 'unresolved');

/**
 * The ONE stage a lead is shown in. Joined, booked and not interested are settled facts about the lead and win over "needs you"; a
 * lead still waiting on a person is "Needs you" before it is "New" or "Talking". Distress is reported separately (a ring, a tint), not
 * as the stage: the stage underneath still says where the lead is.
 */
export function stageOf(f: StageFacts): Exclude<LeadStageKey, 'distress'> {
  if (f.outcome === 'joined') return 'joined';
  if (BOOKED_CODES.includes(f.outcome)) return BOOKED_STAGE;
  if (f.outcome === 'not_interested') return 'not_interested';
  if (needsYou(f)) return 'needs_you';
  return f.contactStatus === 'not_contacted' ? 'new' : 'talking';
}

// ── the plain-English line ───────────────────────────────────────────────────────────────────────────────────────────────────

export interface LineFacts extends StageFacts {
  distress: boolean;
  assistantName: string | null;
  services: Record<ServiceFlag, boolean>;
  /** The kind of the newest thing that happened, from the row's latest activity. */
  latestKind: 'message' | 'call' | 'enquiry' | null;
  /** The first name of the team member who took the newest call, when one did. */
  staffFirstName: string | null;
  /** The booked appointment, with its own zone. */
  booking: { startsAt: string; timezone: string } | null;
  /** True when the club has no AI SMS and no calls: nobody contacts this lead from here. */
  relayOnly?: boolean;
}

const lowerFirst = (s: string): string => (s === '' ? s : s.charAt(0).toLowerCase() + s.slice(1));

/** "Sat 31 Oct, 3:30 pm". Built from parts, in the booking's own zone. */
export const bookingWhen = (b: { startsAt: string; timezone: string }): string => `${dayLabelFor(b.startsAt, b.timezone)}, ${timeLabelFor(b.startsAt, b.timezone)}`;

/**
 * THE ONE PLAIN-ENGLISH LINE under a lead's name in the list. Precedence: distress, then settled outcomes, then what happened last.
 * ⚠️ No message text and no number ever goes in it (it is built from facts, never from what the lead wrote).
 */
export function leadLine(f: LineFacts): string {
  const bot = botNameOf(f.assistantName);
  if (f.distress) return `Texted ${bot} something worrying. Call them today.`;
  if (f.outcome === 'joined') return 'Joined the club';
  if (f.outcome === 'tour_booked' || f.outcome === 'callback_booked') {
    const what = f.outcome === 'tour_booked' ? 'Tour' : 'Call back';
    return f.booking ? `${what} booked by text · ${bookingWhen(f.booking)}` : `${what} booked`;
  }
  if (f.outcome === 'not_interested') return 'Not interested';
  if (f.needsPerson) return 'Needs a person to follow up';
  if (f.services.s2lConnected && f.staffFirstName) {
    const after = f.outcome !== 'unresolved' && f.outcome !== 'no_response' ? ` · ${lowerFirst(LEAD_OUTCOME_LABELS[f.outcome])}` : '';
    return `Spoke with ${f.staffFirstName}${after}`;
  }
  if (f.services.leadCalledIn && f.latestKind === 'call') return 'Called the club';
  if (f.services.leadRepliedToSam) return `Replied to ${bot}`;
  if (f.services.samTexted) return `${bot === 'the assistant' ? 'The assistant' : bot} texted · no reply yet`;
  if (f.services.s2lCallMade) return 'Called · not reached yet';
  if (f.relayOnly) return 'Sent to the club’s CRM';
  return 'New enquiry · not contacted yet';
}

/** The initials avatar's stage tint is the stage's own fill (distress is never an avatar tint: it has its own ring). */

// ── the detail header ────────────────────────────────────────────────────────────────────────────────────────────────────────

/** "Called in 56s" / "Texted in 38s" / "Called in 2 min": the first response, green numeral in the portal. Null when nobody responded. */
export function firstResponseSentence(via: 'sam' | 'call' | null, seconds: number | null): { verb: string; time: string } | null {
  if (via === null || seconds === null) return null;
  const time = seconds < 60 ? `${seconds}s` : seconds < 3600 ? `${Math.floor(seconds / 60)} min` : formatClock(seconds);
  return { verb: via === 'call' ? 'Called in' : 'Texted in', time };
}

/** The weekday of an instant in a zone ("Sat"), for "after the call on Sat". An unreadable instant reads "". */
export function weekdayOf(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', { weekday: 'short', ...(timeZone ? { timeZone } : {}) }).format(d);
}

export interface WhereItsAtFacts {
  outcome: LeadOutcome;
  setBy: 'person' | 'ai' | 'system' | null;
  /** Who confirmed it (a first name), when a person did. */
  confirmedBy: string | null;
  /** The call or conversation the outcome came from. */
  source: 'call' | 'conversation' | null;
  setAt: string | null;
  timeZone?: string;
}

/**
 * "Where it's at": ONE sentence. A person's choice reads as theirs ("Matt confirmed Still deciding after the call on Sat"), an AI
 * suggestion as a question ("Suggested by AI: Still deciding. Confirm?"), nothing as nothing.
 */
export function whereItsAt(f: WhereItsAtFacts): string {
  if (f.outcome === 'unresolved') return 'Nothing confirmed yet.';
  const label = LEAD_OUTCOME_LABELS[f.outcome];
  if (f.setBy === 'ai') return `Suggested by AI: ${label}. Confirm?`;
  if (f.setBy === 'system') return `${label} (set automatically).`;
  const who = f.confirmedBy ? `${f.confirmedBy} confirmed` : 'Confirmed';
  const after = f.source === 'call' ? ' after the call' : f.source === 'conversation' ? ' after the text conversation' : '';
  const day = f.setAt && after ? weekdayOf(f.setAt, f.timeZone) : '';
  return `${who} ${label}${after}${day ? ` on ${day}` : ''}.`;
}

/** The first word of a name, or "them" when there is none: for "Call Ella" / "Text Ella". Strips the test prefix. */
export function firstNameOf(name: string | null | undefined): string {
  const w = (name ?? '').replace(/^\[TEST\]\s*/i, '').trim().split(/\s+/)[0] ?? '';
  return w === '' ? 'them' : w;
}

/** "2d", "5h", "12m", "now": the age beside a name in the list. `nowMs` is an argument. */
export function ageShort(iso: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((nowMs - t) / 60_000));
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

// ── contrast (used by the tests and by nothing at runtime) ─────────────────────────────────────────────────────────────────

const channel = (v: number): number => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const luminance = (hex: string): number => {
  const n = Number.parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};
/** WCAG contrast between two #rrggbb colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

// ── the story (timeline) ───────────────────────────────────────────────────────────────────────────────────────────────────

/** The slice of a call row the title and the chip read. */
export interface CallStoryFacts {
  kind: 'outbound' | 'inbound';
  outcome: string;
  answeredBy: string | null;
  speedToLeadSeconds: number | null;
  /** A call a person started from the lead (Part B): the team member who started it. Absent on every Speed to Lead and inbound call. */
  manualBy?: string | null;
}

/** "Matt called Ella" / "Speed to Lead called Ella" / "Ella called the club". `leadFirst` is the lead's first name ("them" when unknown). */
export function callTitle(c: Pick<CallStoryFacts, 'kind' | 'manualBy'>, leadFirst: string): string {
  if (c.kind === 'inbound') return `${leadFirst === 'them' ? 'They' : leadFirst} called the club`;
  if (c.manualBy) return `${c.manualBy} called ${leadFirst}`;
  return `Speed to Lead called ${leadFirst}`;
}

/**
 * The Speed to Lead chip on an engine call: "Connected in 56s · Matt answered · lead picked up". Only an engine call carries one (a manual
 * or inbound call has no speed). Reads the row's automatic result: a call that did not connect says what it did instead.
 */
export function speedChip(c: CallStoryFacts): string | null {
  if (c.kind !== 'outbound' || c.manualBy || c.speedToLeadSeconds === null) return null;
  const s = c.speedToLeadSeconds;
  const time = s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)} min` : formatClock(s);
  if (c.outcome === 'Connected') return `Connected in ${time}${c.answeredBy ? ` · ${c.answeredBy.trim().split(/\s+/)[0]} answered` : ''} · lead picked up`;
  return `Dialled in ${time} · ${c.outcome}`;
}
